// Helpers for interpreting Stripe Connect account state returned by the
// stripeAccount relationship on currentUser (see user.duck.js).
//
// The currentUser include returns stripeAccount with attributes.stripeAccountId
// populated but stripeAccountData null — the latter requires a separate
// sdk.stripeAccount.fetch(). We therefore treat presence of stripeAccountId
// as "account created and onboarded": users can only get a stripeAccountId
// after completing the Connect account creation flow. Outstanding Stripe
// requirements that develop later aren't detected by this check.

export const isStripeAccountComplete = stripeAccount =>
  !!stripeAccount?.attributes?.stripeAccountId;

// Whether a connected account can take a customer's card payment: Stripe
// lists nothing as currently or past due, and the transfers capability the
// destination charge needs is active (otherwise the payment fails with
// insufficient_capabilities_for_transfer). Data without a capabilities field
// is judged by its requirements alone. `stripeAccountData` comes from
// sdk.stripeAccount.fetch() (fetchStripeAccount), not from currentUser.
const hasDue = (requirements, key) =>
  !!requirements && Array.isArray(requirements[key]) && requirements[key].length > 0;

export const stripeAccountReadyForPayments = stripeAccountData => {
  if (!stripeAccountData) return false;
  const { requirements, capabilities } = stripeAccountData;
  if (hasDue(requirements, 'currently_due') || hasDue(requirements, 'past_due')) return false;
  return !capabilities || capabilities.transfers === 'active';
};

// What the freelancer's "make an offer" modal shows: 'open' (the form) or
// 'blocked' (finish the Stripe account first). Providers who don't use Stripe
// and providers without any account are left to the existing onboarding
// modal; a failed check opens the form, so an outage never locks anyone out.
export const offerStripeGateStatus = ({ requiresStripe, hasStripeAccount, fetchStripeAccount }) => {
  if (!requiresStripe || !hasStripeAccount) return Promise.resolve('open');
  return fetchStripeAccount()
    .then(account =>
      stripeAccountReadyForPayments(account && account.attributes.stripeAccountData)
        ? 'open'
        : 'blocked'
    )
    .catch(() => 'open');
};
